import React, { useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';

const CommonAsyncSelect = ({ 
    value,
    onChange,
    loadOptions,
    isSubmitting,
    errors,
    name,
    label,
    placeholder = "Select...",
    isMulti = false,
    isClearable = true,
    required = false,
    actionButtons = [],
    className = 'mb-3',
    cacheOptions = true,
    defaultOptions = false,
    loadingMessage = () => "Loading...",
    noOptionsMessage = ({ inputValue }) => 
        !inputValue || inputValue.length < 2 
            ? "Type at least 2 characters..." 
            : "No options found",
    debounceTimeout = 500
}) => {
    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
            color: state.isSelected ? 'white' : 'black',
            '&:active': {
                backgroundColor: state.isSelected ? '#0d6efd' : '#e9ecef'
            }
        }),
        control: (provided, state) => ({
            ...provided,
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : provided.boxShadow,
            borderColor: state.isFocused ? '#86b7fe' : errors?.[name] ? '#dc3545' : '#ced4da',
            '&:hover': {
                borderColor: state.isFocused ? '#86b7fe' : errors?.[name] ? '#dc3545' : '#ced4da'
            }
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    // Debounce the loadOptions function
    const debouncedLoadOptions = useCallback(
        debounce((inputValue, callback) => {
            // If loadOptions is a Promise-based function
            if (loadOptions.constructor.name === 'AsyncFunction') {
                loadOptions(inputValue)
                    .then(options => callback(options))
                    .catch(error => {
                        console.error('Error loading options:', error);
                        callback([]);
                    });
            } else {
                // If loadOptions is a callback-based function
                loadOptions(inputValue, callback);
            }
        }, debounceTimeout),
        [loadOptions, debounceTimeout]
    );

    return (
        <div className={`${className}`}>
            {label && (
                <label className="form-label">
                    {label}
                    {required && <span className="text-danger ms-1">*</span>}
                </label>
            )}
            <div className={`d-flex align-items-center ${errors?.[name] ? 'is-invalid' : ''}`}>
                <div className="flex-grow-1">
                    <AsyncSelect
                        value={value}
                        onChange={onChange}
                        loadOptions={debouncedLoadOptions}
                        className="basic-single"
                        classNamePrefix="select"
                        placeholder={placeholder}
                        isDisabled={isSubmitting}
                        isMulti={isMulti}
                        isClearable={isClearable}
                        styles={customStyles}
                        cacheOptions={cacheOptions}
                        defaultOptions={defaultOptions}
                        loadingMessage={loadingMessage}
                        noOptionsMessage={noOptionsMessage}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.label}
                        menuPortalTarget={document.body}
                    />
                </div>
                {actionButtons.map((button, index) => (
                    <button
                        key={index}
                        type="button"
                        className={`btn ${button.className || 'btn-secondary'} ms-2`}
                        onClick={() => button.onClick(value, name)}
                        title={button.title}
                        disabled={isSubmitting || button.disabled}
                    >
                        {button.icon && <i className={`${button.icon}`}></i>}
                        {button.label && <span className={button.icon ? 'ms-1' : ''}>{button.label}</span>}
                    </button>
                ))}
            </div>
            {errors?.[name] && (
                <div className="invalid-feedback">
                    {Array.isArray(errors[name]) ? errors[name][0] : errors[name]}
                </div>
            )}
        </div>
    );
};

export default CommonAsyncSelect; 